import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GlobalStatsChart = () => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGlobalStats();
  }, []);

  const fetchGlobalStats = async () => {
    try {
      const response = await axios.get(`${API}/matches/global-stats`);
      setStats(response.data.slice(0, 10)); // Top 10
    } catch (error) {
      console.error('Failed to fetch global stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBarColor = (winRate) => {
    if (winRate >= 70) return '#10b981'; // Green
    if (winRate >= 50) return '#3b82f6'; // Blue
    if (winRate >= 30) return '#f59e0b'; // Yellow
    return '#ef4444'; // Red
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (stats.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Trophy className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No players yet. Be the first to join!</p>
      </div>
    );
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Player Win Rates
          </h2>
          <p className="text-gray-600">
            See how players are performing across all matches
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-lg">
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              data={stats}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 100, bottom: 20 }}
            >
              <XAxis 
                type="number" 
                domain={[0, 100]} 
                tickFormatter={(value) => `${value}%`}
                stroke="#6b7280"
              />
              <YAxis 
                type="category" 
                dataKey="username" 
                tick={{ fill: '#374151', fontWeight: 500 }}
                width={90}
              />
              <Tooltip 
                formatter={(value, name, props) => [
                  `${value}% win rate`,
                  `${props.payload.wins}W / ${props.payload.losses}L (${props.payload.total_matches} matches)`
                ]}
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '12px'
                }}
              />
              <Bar 
                dataKey="win_rate" 
                radius={[0, 8, 8, 0]}
                barSize={30}
              >
                {stats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.win_rate)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="flex justify-center gap-6 mt-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-500"></div>
              <span className="text-gray-600">70%+ (Pro)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500"></div>
              <span className="text-gray-600">50-70% (Good)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-500"></div>
              <span className="text-gray-600">30-50% (Learning)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-500"></div>
              <span className="text-gray-600">&lt;30% (Beginner)</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlobalStatsChart;
